import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import { Plus, Trash2, Users, X, Upload, Play, GripVertical } from 'lucide-react';

export default function AdminCourses() {
  const { apiClient } = useAuth();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', price: '', curriculum: '', start_date: '' });
  const [lessonForm, setLessonForm] = useState({ courseId: null, title: '', description: '', video_path: '', duration: '' });
  const [uploading, setUploading] = useState(false);

  const fetchCourses = useCallback(async () => {
    try { const { data } = await apiClient.get('/api/courses'); setCourses(data); }
    catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, [apiClient]);

  useEffect(() => { fetchCourses(); }, [fetchCourses]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await apiClient.post('/api/courses', { title: form.title, description: form.description, price: parseFloat(form.price), curriculum: form.curriculum.split('\n').filter(Boolean), start_date: new Date(form.start_date).toISOString() });
      toast.success('Course created!');
      setShowForm(false);
      setForm({ title: '', description: '', price: '', curriculum: '', start_date: '' });
      fetchCourses();
    } catch { toast.error('Failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete?')) return;
    try { await apiClient.delete(`/api/admin/courses/${id}`); toast.success('Deleted'); fetchCourses(); }
    catch { toast.error('Failed'); }
  };

  const handleVideoUpload = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await apiClient.post('/api/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setLessonForm(prev => ({ ...prev, video_path: data.path }));
      toast.success('Video uploaded!');
    } catch { toast.error('Upload failed'); }
    finally { setUploading(false); }
  };

  const handleAddLesson = async (e) => {
    e.preventDefault();
    if (!lessonForm.courseId || !lessonForm.title) return;
    try {
      await apiClient.post(`/api/admin/courses/${lessonForm.courseId}/lesson`, { title: lessonForm.title, description: lessonForm.description, video_path: lessonForm.video_path, duration: lessonForm.duration, order: courses.find(c => c.id === lessonForm.courseId)?.lessons?.length || 0 });
      toast.success('Lesson added!');
      setLessonForm({ courseId: null, title: '', description: '', video_path: '', duration: '' });
      fetchCourses();
    } catch { toast.error('Failed'); }
  };

  const handleDeleteLesson = async (courseId, lessonId) => {
    try { await apiClient.delete(`/api/admin/courses/${courseId}/lesson/${lessonId}`); toast.success('Removed'); fetchCourses(); }
    catch { toast.error('Failed'); }
  };

  const inputCls = "w-full px-3 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white text-sm placeholder-white/30 focus:outline-none focus:ring-1 focus:ring-white/20";

  return (
    <div data-testid="admin-courses-page">
      <div className="flex items-center justify-between mb-8">
        <div><h1 className="text-2xl font-heading font-bold text-white">Courses</h1><p className="text-white/50 text-sm">{courses.length} programs</p></div>
        <button onClick={() => setShowForm(!showForm)} className="bg-gradient-to-r from-blue-500 to-blue-700 text-white px-5 py-2.5 rounded-xl text-sm font-semibold flex items-center gap-2" data-testid="add-course-btn">{showForm ? <X size={16} /> : <Plus size={16} />} {showForm ? 'Cancel' : 'Add Course'}</button>
      </div>

      {showForm && (
        <form onSubmit={handleCreate} className="mb-8 p-6 rounded-2xl border border-white/8 bg-white/[0.03] space-y-4" data-testid="course-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <input placeholder="Course Title" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} required className={inputCls} data-testid="course-title-input" />
            <input type="number" step="0.01" placeholder="Price ($)" value={form.price} onChange={e => setForm({ ...form, price: e.target.value })} required className={inputCls} data-testid="course-price-input" />
          </div>
          <textarea placeholder="Description" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} required rows="2" className={inputCls + " resize-none"} />
          <textarea placeholder="Curriculum (one item per line)" value={form.curriculum} onChange={e => setForm({ ...form, curriculum: e.target.value })} required rows="3" className={inputCls + " resize-none"} />
          <input type="date" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} required className={inputCls} />
          <button type="submit" className="bg-gradient-to-r from-emerald-500 to-emerald-700 text-white px-6 py-2.5 rounded-xl text-sm font-semibold">Create Course</button>
        </form>
      )}

      {loading ? <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-white/40" /></div> : (
        <div className="space-y-6">
          {courses.map(c => (
            <div key={c.id} className="rounded-2xl border border-white/8 bg-white/[0.03] overflow-hidden" data-testid={`admin-course-${c.id}`}>
              <div className="p-5 flex items-start justify-between">
                <div>
                  <h3 className="text-white font-heading font-bold text-lg">{c.title}</h3>
                  <p className="text-white/50 text-xs mt-1">{c.description}</p>
                  <div className="flex gap-4 mt-2">
                    <span className="text-blue-400 font-bold">${c.price}</span>
                    <span className="text-white/40 text-xs flex items-center gap-1"><Users size={12} /> {c.enrolled_count} enrolled</span>
                    <span className="text-white/40 text-xs flex items-center gap-1"><Play size={12} /> {c.lessons?.length || 0} lessons</span>
                  </div>
                </div>
                <button onClick={() => handleDelete(c.id)} className="p-2 rounded-lg text-white/20 hover:text-red-400 hover:bg-red-500/10"><Trash2 size={14} /></button>
              </div>

              {/* Lessons */}
              <div className="border-t border-white/5 p-4">
                <h4 className="text-white/60 text-xs font-bold uppercase tracking-wider mb-3">Lessons / Videos</h4>
                {(c.lessons || []).length > 0 ? (
                  <div className="space-y-2 mb-4">
                    {c.lessons.map((l, idx) => (
                      <div key={l.id} className="flex items-center gap-3 p-3 rounded-xl bg-white/[0.03] border border-white/5">
                        <GripVertical size={14} className="text-white/20" />
                        <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center"><Play size={12} className="text-blue-300" /></div>
                        <div className="flex-1 min-w-0">
                          <p className="text-white text-sm font-semibold truncate">{l.title}</p>
                          <p className="text-white/40 text-xs">{l.duration || 'No duration'}{l.video_path ? ' - Video uploaded' : ' - No video'}</p>
                        </div>
                        <button onClick={() => handleDeleteLesson(c.id, l.id)} className="p-1 text-white/20 hover:text-red-400"><Trash2 size={12} /></button>
                      </div>
                    ))}
                  </div>
                ) : <p className="text-white/30 text-xs mb-4">No lessons yet</p>}

                {/* Add Lesson Form */}
                {lessonForm.courseId === c.id ? (
                  <form onSubmit={handleAddLesson} className="space-y-3 p-4 rounded-xl bg-white/[0.02] border border-white/5">
                    <input placeholder="Lesson title" value={lessonForm.title} onChange={e => setLessonForm(prev => ({ ...prev, title: e.target.value }))} required className={inputCls} />
                    <div className="grid sm:grid-cols-2 gap-3">
                      <input placeholder="Duration (e.g. 15 min)" value={lessonForm.duration} onChange={e => setLessonForm(prev => ({ ...prev, duration: e.target.value }))} className={inputCls} />
                      <div>
                        {lessonForm.video_path ? (
                          <div className="flex items-center gap-2 p-2.5 rounded-xl bg-green-500/10 border border-green-500/20 text-green-300 text-xs">
                            <Play size={12} /> Video uploaded
                            <button onClick={() => setLessonForm(prev => ({ ...prev, video_path: '' }))} className="ml-auto text-red-400"><X size={12} /></button>
                          </div>
                        ) : (
                          <label className="flex items-center gap-2 p-2.5 rounded-xl bg-white/5 border border-dashed border-white/20 cursor-pointer hover:border-blue-400/50 text-white/40 text-xs">
                            <Upload size={14} /> {uploading ? 'Uploading...' : 'Upload Video'}
                            <input type="file" accept="video/*" className="hidden" onChange={e => { if (e.target.files[0]) handleVideoUpload(e.target.files[0]); }} disabled={uploading} />
                          </label>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button type="button" onClick={() => setLessonForm({ courseId: null, title: '', description: '', video_path: '', duration: '' })} className="px-4 py-2 text-xs text-white/50 rounded-lg hover:bg-white/5">Cancel</button>
                      <button type="submit" className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-xs font-semibold">Add Lesson</button>
                    </div>
                  </form>
                ) : (
                  <button onClick={() => setLessonForm(prev => ({ ...prev, courseId: c.id }))} className="text-xs text-blue-300 hover:text-blue-200 font-semibold flex items-center gap-1"><Plus size={12} /> Add Lesson</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
